import { computed, inject, Injectable, signal } from '@angular/core';
import { Language } from '@core/models/language';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { stringToBoolean } from 'src/app/utils/string-to-boolean';
import { ApplicationRedirectionService } from './application-redirection.service';
import { AuthenticationService } from './authentication.service';
import { LANGUAGE_KEY } from './culture-resolver.service';

export const RETURN_URL_PARAM_KEY = 'ReturnUrl';
export const NO_REDIRECT_PARAM_KEY = 'noRedirect';
export const RESOURCE_PARAM_KEY = 'resource';

type ContextState = {
    language: Language | null;
    resourceType: string | null;
    returnUrl: string | null;
    noRedirect: boolean;
    isLoaded: boolean;
};
@Injectable({
    providedIn: 'root',
})
export class ContextService {
    private readonly authenticationApi = inject(AuthenticationService);
    private readonly applicationRedirection = inject(ApplicationRedirectionService);

    private readonly state = signal<ContextState>({
        resourceType: null,
        language: null,
        returnUrl: null,
        noRedirect: false,
        isLoaded: false,
    });

    readonly resourceType = computed(() => this.state().resourceType);
    readonly language = computed(() => this.state().language);
    readonly returnUrl = computed(() => this.state().returnUrl);
    readonly noRedirect = computed(() => this.state().noRedirect);
    readonly isLoaded = computed(() => this.state().isLoaded);

    initialize(): Observable<unknown> {
        const url = new URL(window.location.href);
        const returnUrl = url.searchParams.get(RETURN_URL_PARAM_KEY);
        const noRedirect = stringToBoolean(url.searchParams.get(NO_REDIRECT_PARAM_KEY));
        const resourceType = url.searchParams.get(RESOURCE_PARAM_KEY);
        const language = url.searchParams.get(LANGUAGE_KEY) as Language;

        if (returnUrl) {
            return this.authenticationApi.context(returnUrl).pipe(
                switchMap((context) => {
                    // in this specific case, we should log the user in using the context data.
                    // This only happens in application that want to log the user in automatically during the registration process.
                    // For example, in Citizen application, the user needs to be logged in to receive some documents.
                    if (context.login) {
                        return this.authenticationApi
                            .profiles({ identifiant: context.login, password: context.password! }, this.language() ?? 'fr')
                            .pipe(
                                switchMap((profiles) => {
                                    // Bypass automatic logout in account-activated.guard even if
                                    // the account has not been activated yet
                                    this.authenticationApi.bypassAutomaticLogout = true;
                                    return this.authenticationApi.login({
                                        identifiant: context.login!,
                                        password: context.password!,
                                        personnePersonneTypeId: profiles.profils[0].personnePersonneTypeID,
                                        personneType: profiles.profils[0].personneType,
                                        rememberMe: false,
                                        returnUrl,
                                    });
                                }),
                                switchMap((loginResponse) =>
                                    this.applicationRedirection.redirect$(
                                        loginResponse.returnUrl,
                                        context.resourceType ?? null,
                                        context.language ?? language ?? 'fr',
                                    ),
                                ),
                                map(() => context),
                                tap(() => {
                                    console.log('User logged in using context data');
                                }),
                            );
                    } else {
                        return of(context);
                    }
                }),
                tap((context) => {
                    this.updateState({ resourceType, language, ...context, returnUrl, noRedirect });
                }),
            );
        } else {
            this.updateState({ noRedirect, language, resourceType });
            return of(true);
        }
    }

    updateState(state: Partial<ContextState>) {
        this.state.update((currentState) => ({ ...currentState, ...state, isLoaded: true }));
    }
}
